import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, LogOut, CheckCircle2 } from "lucide-react";
import CodeLogin from "@/components/CodeLogin";
import FamilyForm from "@/components/FamilyForm";
import StatusBadge from "@/components/StatusBadge";
import PayQRCodes from "@/components/PayQRCodes";
import { client, getToken, setToken, errMsg, useOverview } from "@/lib/api";
import { money } from "@/lib/format";

function Summary({ fam, data }) {
  const acts = (data?.activities || []).map((a) => ({ a, s: fam.activity_signups?.[a.id] || {} })).filter(({ s }) => (s.adults || 0) + (s.keiki || 0) > 0)
    .map(({ a, s }) => `${a.name} (${s.adults || 0} adult${s.adults === 1 ? "" : "s"}, ${s.keiki || 0} keiki)`);
  const teams = (data?.teams || []).filter((t) => fam.team_ids?.includes(t.id)).map((t) => t.name);
  const shirts = Object.entries(fam.shirt_orders || {}).filter(([, q]) => q > 0).map(([s, q]) => `${q} × ${s}`);
  return (
    <div className="space-y-6">
      <div className="card-warm" data-testid="family-summary">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 data-testid="my-family-name" className="text-2xl sm:text-3xl font-bold text-amber-900">{fam.family_name}</h2>
          {fam.checked_in && <span className="flex items-center gap-2 text-green-800 font-bold"><CheckCircle2 className="w-6 h-6" />Checked in</span>}
        </div>
        <p className="text-lg text-stone-700 mt-1">{fam.contact_name}{fam.email && ` · ${fam.email}`}</p>
        <ul className="mt-5 grid sm:grid-cols-2 gap-3">
          {fam.members.map((m) => <li key={m.id} className="rounded-xl bg-[#FDF6ED] px-4 py-3 text-lg font-semibold">{m.name}{m.age ? <span className="font-normal text-stone-600"> · {m.age}</span> : null}</li>)}
        </ul>
        {acts.length > 0 && <div className="mt-5"><p className="label-cap mb-2">Activities</p><ul className="text-lg space-y-1" data-testid="my-family-activities">{acts.map((a) => <li key={a}>{a}</li>)}</ul></div>}
        {shirts.length > 0 && <p className="mt-4 text-lg" data-testid="my-family-shirts">T-shirts: <b>{shirts.join(", ")}</b></p>}
        {teams.length > 0 && <p className="mt-4 text-lg" data-testid="my-family-teams">Helping with: <b>{teams.join(", ")}</b></p>}
      </div>
      <div className="card-warm" data-testid="family-payment-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-2xl sm:text-3xl font-bold text-amber-900">Payment</h2>
          <StatusBadge status={fam.pay_status} testId="my-family-pay-status" />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5 text-center">
          {[["Due", fam.amount_due], ["Paid", fam.amount_paid], ["Balance", fam.balance]].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-[#FDF6ED] p-3"><p className="label-cap">{l}</p><p data-testid={`my-family-${l.toLowerCase()}`} className="font-heading text-xl sm:text-2xl font-bold mt-1">{money(v)}</p></div>
          ))}
        </div>
        {fam.payments?.length > 0 && <ul className="mt-5 divide-y divide-amber-900/10">{fam.payments.map((p) => <li key={p.id} className="py-3 flex justify-between text-lg"><span>{p.date} {p.method && `· ${p.method}`}</span><b>{money(p.amount)}</b></li>)}</ul>}
        {data?.settings?.payment_instructions && <p className="mt-5 text-lg whitespace-pre-line rounded-xl bg-amber-50 p-4"><b>How to pay: </b>{data.settings.payment_instructions}</p>}
        {fam.balance > 0 && <div className="mt-5"><PayQRCodes /></div>}
      </div>
    </div>
  );
}

export default function MyFamily() {
  const { data } = useOverview();
  const [fam, setFam] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!getToken("family"));

  const load = useCallback(async () => {
    if (!getToken("family")) return;
    try {
      setFam((await client("family").get("/family/me")).data);
    } catch {
      setToken("family", null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setBusy(true);
    try {
      setFam((await client("family").put("/family/me", payload)).data);
      setEditing(false);
      toast.success("Your changes are saved. Faʻafetai!");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => { setToken("family", null); setFam(null); setEditing(false); };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
      <p className="label-cap">Your registration</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-amber-950 mt-2 mb-8">My Family</h1>
      {loading ? <p className="text-lg">Loading…</p> : !fam ? (
        <div className="space-y-6">
          <CodeLogin onVerified={(d) => { setToken("family", d.token); setFam(d.family); }} />
          <p className="text-lg">Not registered yet? <Link to="/register" data-testid="myfamily-register-link" className="underline font-bold">Register your family</Link></p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-3">
            {!editing && <button data-testid="edit-registration-button" onClick={() => setEditing(true)} className="btn-primary"><Pencil className="w-5 h-5" />Change my registration</button>}
            {editing && <button data-testid="cancel-edit-button" onClick={() => setEditing(false)} className="btn-outline">Cancel changes</button>}
            <button data-testid="family-signout-button" onClick={signOut} className="btn-outline"><LogOut className="w-5 h-5" />Sign out</button>
          </div>
          {editing ? <FamilyForm initial={fam} data={data} onSubmit={save} busy={busy} submitLabel="Save my changes" /> : <Summary fam={fam} data={data} />}
        </div>
      )}
    </div>
  );
}
